import React, { useState } from "react";
import { ApiUIFilter, filterFunctionType } from "./types";
import { getSideFilterQueryKey } from "./dataloader";
import { useCustomNavigation } from "apps/console/components/navigation/context";
import { useQueryClient } from "react-query";
import { filterFunction } from "shared/utils";
import { useLocation } from "react-router-dom";
import { SideFilterKey } from "./enum";
import { NavOptionType } from "apps/console/components/navigation/context/type";

/**
 * append filter count in filters
 */
function appendFilterCountInLabel({
  filterConfig,
  data,
  key,
  filter
}: {
  filterConfig: NavOptionType;
  data: Array<any>;
  key?: string;
  filter: filterFunctionType;
}): NavOptionType {
  const filterKey = filterConfig.filterKey || key;
  const filterData = filterConfig?.showCount ? filter({ data, key: filterKey, value: filterConfig?.param }) : data;

  const child =
    filterConfig?.child && filterConfig?.child.length > 0
      ? filterConfig?.child.map(item =>
          appendFilterCountInLabel({ filterConfig: item, data: filterData, key: filterConfig.key, filter })
        )
      : undefined;

  return {
    ...filterConfig,
    itemCount: filterConfig?.displayLabel && filterConfig?.showCount ? filterData.length : undefined,
    child,
    className: "navigationFilters"
  };
}

/**
 * Hook for showing filters on listing screen
 * @param key
 * @param filterParentKey : if we want to map multiple filter keys to single parent key then we can use this, ex: in workflow there will be different filter object for each screen mapped to single "workflow" navItem
 */
export default function useSideFilters(
  key: SideFilterKey,
  filterParentKey?: string,
  filter: filterFunctionType = filterFunction
) {
  const { updateNavOptions, removeNavOptions } = useCustomNavigation();
  const location = useLocation();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    return () => {
      removeNavOptions?.({ key: filterParentKey || key } as NavOptionType);
    };
  }, [removeNavOptions, filterParentKey, key]);

  const [path] = useState(() => {
    const pathArray = location.pathname.split("/");
    return pathArray[pathArray.length - 1];
  });

  const updateFilter = React.useCallback(
    (data?: Array<any>) => {
      const config: ApiUIFilter | undefined = queryClient.getQueryData(getSideFilterQueryKey());
      if (config && data) {
        const filterConfigForKey = config.apiConsoleFilters?.find(obj => obj.key === key);
        if (filterConfigForKey) {
          const currentFilterConfig = {
            ...appendFilterCountInLabel({
              filterConfig: filterConfigForKey,
              data,
              key,
              filter
            }),
            key: filterParentKey || key,
            path
          };
          updateNavOptions?.(currentFilterConfig);
        }
      }
    },
    [filter, filterParentKey, key, path, queryClient, updateNavOptions]
  );

  /**
   * Call this function from Listing page and pass listing Data
   */
  const updateListData = React.useCallback(
    (data: Array<any>) => {
      updateFilter(data);
    },
    [updateFilter]
  );

  return updateListData;
}
